import { useRef } from "react";
import { useController } from "react-hook-form";
import { useBoolean } from "@miplatform/hooks";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

export const MiControlledTimerPicker = ({
  value,
  label,
  name,
  helperText = null,
  formControl: control = {},
  rules = {},
  valueModifier,
  disabled,
  sx = {},
  className,
  required,
  format = "HH:mm",
  ampm,
  strictInterval,
  minutesStep,
  slotProps,
  ...props
}) => {
  const [open, handleOpen, handleClose] = useBoolean();
  const isOpenRef = useRef(false);
  const {
    field: { onChange, onBlur, value: defaultValue, ref },
    fieldState: { error },
  } = useController({ name, control, rules });

  const setOpen = (nextOpen) => {
    isOpenRef.current = nextOpen;
    if (nextOpen) {
      handleOpen();
    } else {
      handleClose();
    }
  };

  const commitValue = (newValue) => {
    onChange(valueModifier ? valueModifier(newValue) : newValue);
  };

  const handlePickerChange = (newValue) => {
    if (!isOpenRef.current) {
      commitValue(newValue);
    }
  };

  const handleAccept = (newValue) => {
    commitValue(newValue);
    setOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <TimePicker
        {...props}
        inputRef={ref}
        label={label}
        value={value ?? defaultValue ?? null}
        required={required}
        format={format}
        onChange={handlePickerChange}
        onAccept={handleAccept}
        disabled={disabled}
        error={!!error}
        helperText={error ? error.message : helperText}
        ampm={ampm}
        minutesStep={minutesStep}
        closeOnSelect={false}
        open={open}
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        slotProps={{
          ...slotProps,
          actionBar: {
            actions: ["cancel", "accept"],
            ...slotProps?.actionBar,
          },
          popper: {
            disablePortal: true,
            ...slotProps?.popper,
          },
          textField: {
            size: "small",
            fullWidth: true,
            error: !!error,
            helperText: error ? error.message : helperText,
            required,
            onBlur,
            inputProps: {
              ref,
              "data-testid": `mi-time-picker-${name}`,
              readOnly: !!strictInterval,
              style: { cursor: "pointer" },
              onClick: () => {
                if (strictInterval) {
                  setOpen(true);
                }
              },
            },
            sx: {
              ...sx,
            },
            ...slotProps?.textField,
          },
        }}
      />
    </LocalizationProvider>
  );
};
